import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import CarListingForm from './CarListingForm';

const CarListingManager = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  const [showForm, setShowForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const records = await pb.collection('car_listings').getFullList({
        sort: '-created',
      });
      setListings(records);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la récupération des annonces:', err);
      setError('Impossible de charger les annonces.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingListing(null);
    setShowForm(true);
  };

  const handleEditClick = (listing) => {
    setEditingListing(listing);
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
      try {
        await pb.collection('car_listings').delete(id);
        setListings(prev => prev.filter(l => l.id !== id));
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        alert('Erreur lors de la suppression de l\'annonce.');
      }
    }
  };

  const handleFormSubmit = async (submitData) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', submitData.title);
      formData.append('description', submitData.description);
      formData.append('price', submitData.price);
      formData.append('make', submitData.make);
      formData.append('model', submitData.model);
      formData.append('year', submitData.year);
      formData.append('mileage', submitData.mileage);
      formData.append('fuel_type', submitData.fuel_type);
      formData.append('transmission', submitData.transmission);
      formData.append('status', submitData.status);
      formData.append('location', submitData.location);
      if (submitData.youtube_link !== undefined) {
        formData.append('youtube_link', submitData.youtube_link);
      }
      
      // Affecter l'auteur connecté (vendeur)
      if (!editingListing) {
        formData.append('seller_id', pb.authStore.model.id);
      }

      // Gestion des images existantes (les images que l'on garde)
      if (editingListing && submitData.existingImages && submitData.existingImages.length > 0) {
        for (const existingName of submitData.existingImages) {
          formData.append('images', existingName);
        }
      }

      // Ajout de la photo de couverture
      if (submitData.coverImageFile) {
        formData.append('cover_image', submitData.coverImageFile);
      }

      // Ajout des nouvelles images (galerie)
      if (submitData.imageFiles && submitData.imageFiles.length > 0) {
        for (const file of submitData.imageFiles) {
          formData.append('images', file);
        }
      }

      if (editingListing) {
        await pb.collection('car_listings').update(editingListing.id, formData);
      } else {
        await pb.collection('car_listings').create(formData);
      }

      await fetchListings();
      setShowForm(false);
      setEditingListing(null);
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement:', err);
      alert('Une erreur est survenue lors de l\'enregistrement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available': return <span className="badge badge-success">Disponible</span>;
      case 'reserved': return <span className="badge badge-warning">Réservé</span>;
      case 'sold': return <span className="badge badge-error">Vendu</span>;
      default: return <span className="badge">{status}</span>;
    }
  };

  return (
    <div className="tab-pane active">
      <div className="section-header">
        <div>
          <h2 className="section-title">Annonces Auto</h2>
          <p className="section-subtitle">Gérez les véhicules en vente sur la plateforme</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} 
              onClick={() => setViewMode('list')}
              title="Vue Liste"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6"></line>
                <line x1="8" y1="12" x2="21" y2="12"></line>
                <line x1="8" y1="18" x2="21" y2="18"></line>
                <line x1="3" y1="6" x2="3.01" y2="6"></line>
                <line x1="3" y1="12" x2="3.01" y2="12"></line>
                <line x1="3" y1="18" x2="3.01" y2="18"></line>
              </svg>
            </button>
            <button 
              className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} 
              onClick={() => setViewMode('grid')}
              title="Vue Grille"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
            </button>
          </div>
          <button className="btn btn-primary" onClick={handleAddClick}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Ajouter un véhicule
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-state">
          <div className="loader"></div>
          <p>Chargement des annonces...</p>
        </div>
      ) : listings.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🚗</div>
          <h3>Aucun véhicule en vente</h3>
          <p>Commencez par ajouter votre premier véhicule d'occasion.</p>
          <button className="btn btn-primary" onClick={handleAddClick}>
            Ajouter un véhicule
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Véhicule</th>
                <th>Prix</th>
                <th>Année / Km</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {listings.map(listing => (
                <tr key={listing.id}>
                  <td>
                    <div className="vehicle-info">
                      <div className="vehicle-img">
                        {listing.cover_image ? (
                          <img 
                            src={`https://christophedepanne.infocolab.online/api/files/car_listings/${listing.id}/${listing.cover_image}?thumb=100x100`} 
                            alt={listing.title} 
                          />
                        ) : listing.images && listing.images.length > 0 ? (
                          <img 
                            src={`https://christophedepanne.infocolab.online/api/files/car_listings/${listing.id}/${listing.images[0]}?thumb=100x100`} 
                            alt={listing.title} 
                          />
                        ) : (
                          <div className="no-img">🚗</div>
                        )}
                      </div>
                      <div>
                        <strong>{listing.title}</strong>
                        <div className="text-sm text-gray">{listing.make} {listing.model}</div>
                      </div>
                    </div>
                  </td>
                  <td className="font-medium">{listing.price} €</td>
                  <td>
                    {listing.year} <br/>
                    <span className="text-sm text-gray">{listing.mileage} km</span>
                  </td>
                  <td>{getStatusBadge(listing.status)}</td>
                  <td>{new Date(listing.created).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon edit-btn" onClick={() => handleEditClick(listing)} title="Modifier">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button className="btn-icon delete-btn" onClick={() => handleDeleteClick(listing.id)} title="Supprimer">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid-view">
          {listings.map(listing => (
            <div className="grid-card" key={listing.id}>
              <div className="card-image">
                {listing.cover_image ? (
                  <img 
                    src={`https://christophedepanne.infocolab.online/api/files/car_listings/${listing.id}/${listing.cover_image}?thumb=400x300`} 
                    alt={listing.title} 
                  />
                ) : listing.images && listing.images.length > 0 ? (
                  <img 
                    src={`https://christophedepanne.infocolab.online/api/files/car_listings/${listing.id}/${listing.images[0]}?thumb=400x300`} 
                    alt={listing.title} 
                  />
                ) : (
                  <div className="no-img-large">🚗</div>
                )}
                <div className="card-badge">{getStatusBadge(listing.status)}</div>
              </div>
              <div className="card-content">
                <div className="card-header">
                  <h3>{listing.title}</h3>
                  <div className="card-price">{listing.price} €</div>
                </div>
                <div className="card-meta">
                  <span>{listing.make} {listing.model}</span>
                </div>
                <div className="card-details">
                  <div className="detail-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                    {listing.year}
                  </div>
                  <div className="detail-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    {listing.mileage} km
                  </div>
                </div>
                <div className="card-actions">
                  <button className="btn btn-outline" onClick={() => handleEditClick(listing)}>Modifier</button>
                  <button className="btn btn-outline text-error" onClick={() => handleDeleteClick(listing.id)}>Supprimer</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <CarListingForm
          listing={editingListing}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowForm(false)}
          loading={isSubmitting}
        />
      )}

      <style jsx>{`
        .vehicle-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .vehicle-img {
          width: 48px;
          height: 48px;
          border-radius: 6px;
          overflow: hidden;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .vehicle-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .no-img { font-size: 24px; }
        .text-sm { font-size: 0.85rem; }
        .text-gray { color: #6b7280; }
        .font-medium { font-weight: 500; }
        
        .badge { display: inline-block; padding: 4px 8px; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; }
        .badge-success { background: #d1fae5; color: #065f46; }
        .badge-warning { background: #fef3c7; color: #92400e; }
        .badge-error { background: #fee2e2; color: #b91c1c; }

        .header-actions { display: flex; align-items: center; gap: 12px; }
        .view-toggle {
          display: flex; background: #f3f4f6; padding: 4px; border-radius: 8px;
        }
        .toggle-btn {
          background: transparent; border: none; padding: 6px 10px; border-radius: 6px;
          color: #6b7280; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; justify-content: center;
        }
        .toggle-btn:hover { color: #111827; }
        .toggle-btn.active { background: white; color: #111827; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }

        .action-buttons { display: flex; gap: 8px; }
        .btn-icon {
          background: #f3f4f6; border: none; padding: 8px; border-radius: 6px; color: #4b5563; cursor: pointer; transition: all 0.2s;
          display: flex; align-items: center; justify-content: center;
        }
        .btn-icon:hover { background: #e5e7eb; color: #111827; }
        .btn-icon.edit-btn:hover { background: #e0f2fe; color: #0284c7; }
        .btn-icon.delete-btn { color: #ef4444; }
        .btn-icon.delete-btn:hover { background: #fee2e2; color: #dc2626; }

        /* Grid View Styles */
        .grid-view {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; padding: 20px 0;
        }
        .grid-card {
          background: white; border-radius: 12px; overflow: hidden; border: 1px solid #e5e7eb;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); transition: transform 0.2s, box-shadow 0.2s;
        }
        .grid-card:hover { transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
        .card-image {
          position: relative; height: 180px; background: #f3f4f6; display: flex; align-items: center; justify-content: center;
        }
        .card-image img { width: 100%; height: 100%; object-fit: cover; }
        .no-img-large { font-size: 48px; }
        .card-badge { position: absolute; top: 12px; right: 12px; }
        .card-content { padding: 16px; }
        .card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
        .card-header h3 { margin: 0; font-size: 1.1rem; color: #111827; }
        .card-price { font-weight: 700; color: #111827; font-size: 1.1rem; }
        .card-meta { color: #6b7280; font-size: 0.9rem; margin-bottom: 12px; }
        .card-details {
          display: flex; gap: 16px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb;
        }
        .detail-item { display: flex; align-items: center; gap: 6px; font-size: 0.85rem; color: #4b5563; }
        .card-actions { display: flex; gap: 12px; }
        .btn-outline {
          flex: 1; background: transparent; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px;
          font-size: 0.85rem; font-weight: 500; cursor: pointer; transition: all 0.2s; color: #374151;
        }
        .btn-outline:hover { background: #f9fafb; border-color: #d1d5db; }
        .btn-outline.text-error { color: #dc2626; border-color: #fca5a5; }
        .btn-outline.text-error:hover { background: #fef2f2; border-color: #f87171; }
      `}</style>
    </div>
  );
};

export default CarListingManager;
